import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, RefreshCw, Check, X, Trash2 } from 'lucide-react';
import { API_URL } from '@/utils/api';
import { getCachedAdminPassword, verifyAndEnableAdminAccess } from '@/lib/entitlement';

const API = API_URL;

function SubmissionCard({ item, headers, onChanged }) {
  const [title, setTitle] = useState(item.admin_title ?? item.title ?? '');
  const [category, setCategory] = useState(item.admin_category || '');
  const [altText, setAltText] = useState(item.admin_alt_text || '');
  const [busy, setBusy] = useState(false);

  const patch = async (body) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/gallery-submissions/${item.id}`, body, { headers });
      toast.success('Updated');
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove this submission permanently?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/gallery-submissions/${item.id}`, { headers });
      toast.success('Removed');
      onChanged();
    } catch (err) {
      toast.error('Failed to remove');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden flex flex-col" data-testid={`gallery-submission-${item.id}`}>
      <img src={`${API}/gallery-submissions/image/${item.filename}`} alt={altText || title || 'Submitted plan'} className="w-full h-48 object-contain bg-gray-50 border-b border-gray-100" data-testid={`gallery-submission-image-${item.id}`} />
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div className="flex items-center justify-between text-xs">
          <span className={`rounded-full px-2 py-0.5 font-medium ${
            item.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : item.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
          }`} data-testid={`gallery-submission-status-${item.id}`}>{item.status}</span>
          <span className="text-gray-400">{item.created_at ? new Date(item.created_at).toLocaleDateString('en-GB') : '—'}</span>
        </div>
        <div className="text-xs text-gray-500">
          Submitted title: <span className="text-gray-700">{item.title || '(none)'}</span>
        </div>
        <div className="text-xs text-gray-500">
          Attribution: <span className="text-gray-700">
            {item.attribution_type === 'anonymous' ? 'Anonymous' : `${item.attribution_type === 'first_name' ? 'First name' : 'Business'}: ${item.attribution_value || '—'}`}
          </span>
        </div>
        {item.consent_show_feedback && <div className="text-xs text-blue-600">Consented to show feedback comment alongside</div>}

        <div className="mt-1 space-y-1">
          <Label className="text-[11px] font-medium text-gray-500">Public title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-8 text-sm" data-testid={`gallery-submission-title-input-${item.id}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px] font-medium text-gray-500">Category</Label>
          <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Flat, House, Extension" className="h-8 text-sm" data-testid={`gallery-submission-category-input-${item.id}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px] font-medium text-gray-500">Alt text</Label>
          <Input value={altText} onChange={(e) => setAltText(e.target.value)} className="h-8 text-sm" data-testid={`gallery-submission-alt-input-${item.id}`} />
        </div>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => patch({ admin_title: title, admin_category: category, admin_alt_text: altText })} data-testid={`gallery-submission-save-${item.id}`}>
          Save details
        </Button>

        <div className="mt-auto flex items-center gap-2 pt-2">
          {item.status !== 'approved' && (
            <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => patch({ status: 'approved' })} data-testid={`gallery-submission-approve-${item.id}`}>
              <Check className="h-4 w-4 mr-1" /> Approve
            </Button>
          )}
          {item.status !== 'rejected' && (
            <Button size="sm" variant="outline" className="flex-1" disabled={busy} onClick={() => patch({ status: 'rejected' })} data-testid={`gallery-submission-reject-${item.id}`}>
              <X className="h-4 w-4 mr-1" /> Reject
            </Button>
          )}
          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" disabled={busy} onClick={remove} data-testid={`gallery-submission-remove-${item.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

const AdminGallery = () => {
  const [password, setPassword] = useState(getCachedAdminPassword());
  const [passwordInput, setPasswordInput] = useState('');
  const [unlocked, setUnlocked] = useState(!!getCachedAdminPassword());
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending_count: 0, approved_count: 0, rejected_count: 0 });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(false);

  const headers = { 'x-admin-password': password };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/gallery-submissions?status=${statusFilter}`, { headers });
      setItems(res.data.items || []);
      setCounts({ pending_count: res.data.pending_count || 0, approved_count: res.data.approved_count || 0, rejected_count: res.data.rejected_count || 0 });
    } catch (err) {
      toast.error('Failed to load gallery submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (unlocked) fetchItems(); }, [unlocked, statusFilter]);

  const login = async (e) => {
    e.preventDefault();
    try {
      await verifyAndEnableAdminAccess(passwordInput);
      setPassword(passwordInput);
      setUnlocked(true);
    } catch {
      toast.error('Incorrect admin password');
    }
  };

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-testid="admin-gallery-login-title">Gallery Moderation</h1>
            <form onSubmit={login} className="space-y-4">
              <div>
                <Label htmlFor="gallery-admin-password">Admin password</Label>
                <Input id="gallery-admin-password" type="password" value={passwordInput} onChange={(e) => setPasswordInput(e.target.value)} required className="mt-1" data-testid="admin-gallery-password-input" />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="admin-gallery-login-btn">Unlock</Button>
            </form>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <Link to="/admin"><Button variant="outline" size="sm" className="mb-4"><ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard</Button></Link>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="admin-gallery-title">HPD Draw Gallery Moderation</h1>
            <p className="text-gray-600 mt-1">Review plans users voluntarily shared. Nothing publishes automatically.</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44" data-testid="admin-gallery-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending ({counts.pending_count})</SelectItem>
                <SelectItem value="approved">Approved ({counts.approved_count})</SelectItem>
                <SelectItem value="rejected">Rejected ({counts.rejected_count})</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchItems} data-testid="admin-gallery-refresh"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-gray-500" data-testid="admin-gallery-empty">No submissions match this filter.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item) => (
              <SubmissionCard key={item.id} item={item} headers={headers} onChanged={fetchItems} />
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AdminGallery;
